import * as React from "react";
import { FunctionComponent, useState, useEffect } from "react";
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input/input';
import { isValidPhoneNumber, formatPhoneNumber, isPossiblePhoneNumber } from "react-phone-number-input";
import { GeneralLiabilityClaimReport, ClaimType } from "../../../Models/Claim";
import FileDragAndDrop from "../../FileDragAndDrop";
import { FilesTable } from "../../FilesTable";
import ReactTooltip from 'react-tooltip';
import { ByteLimit, AfterToolTipHide, AfterToolTipShow, ValidateEmail } from "../../../Utils";
import { Collapse, ProgressBar } from "react-bootstrap";
import Server from "../../../Server";

interface StepFourProps {
	goBack: (step: number, claim: GeneralLiabilityClaimReport) => void;
    submit: (claim: GeneralLiabilityClaimReport) => void;
	claim: GeneralLiabilityClaimReport;
	showError: boolean;
    doGoBackCallback: boolean;
    goBackCallbackStep: number;
    doSubmit: boolean;
}

export const StepFour: FunctionComponent<StepFourProps> = ({ goBack, claim, showError, doGoBackCallback, goBackCallbackStep, doSubmit, submit}) => {
	//this is the general liability step four.
	const [shouldScroll, setShouldScroll] = useState<boolean>(true);
	const [width, setWidth] = useState<number>(window.innerWidth);
	const [areWitnesses, setAreWitnesses] = useState<string>(getLocalStorage("generalclaim-witnesses"));
	const [witnessName, setWitnessName] = useState<string>(claim.witnessName);
	const [witnessAddress, setWitnessAddress] = useState<string>(claim.witnessAddress);
	const [witnessPrimaryPhone, setWitnessPrimaryPhone] = useState<string>(claim.witnessPrimaryPhone);
	const [witnessAlternatePhone, setWitnessAlternatePhone] = useState<string>(claim.witnessAlternatePhone);
	const [witnessEmail, setWitnessEmail] = useState<string>(claim.witnessName);
	const [remarks, setRemarks] = useState<string>(claim.remarks);

	function getLocalStorage(key: string) {
		return localStorage.getItem(key) || ""
	}

	useEffect(() => {
		if (shouldScroll) {
			window.scrollTo({
				top: 0,
				behavior: "smooth"
			});
			setShouldScroll(false);
		}
	}, [shouldScroll])

    useEffect(() =>{
        if (showError)
		{
			//This is empty for now, need to figure out why this was here in the autop claim wizard.
        }
    },[showError]);

    useEffect(() =>{
        if (doGoBackCallback && goBackCallbackStep !== -1)
            goToStep(goBackCallbackStep);
    }, [doGoBackCallback])

    useEffect(() =>{
        if (doSubmit)
            submitClaim();
    }, [doSubmit])

	function handleWindowSizeChange() {
		setWidth(window.innerWidth);
	}


	useEffect(() => {
		window.addEventListener('resize', handleWindowSizeChange);
		return () => {
			window.removeEventListener('resize', handleWindowSizeChange);
		}
	}, []);

	let isMobile: boolean = (width <= 768);

	return <div className="step-four">
		<div className="form-group witness-dropdown">
			<label className="control-label col-lg-6 col-md-6 col-sm-12 col-xs-12" htmlFor="any-witnesses">Were there any witnesses?<span className="red-ast">*</span></label>
            <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
				<select className="form-control" id="any-witnesses" area-required aria-invalid={showError && areWitnesses === ""} aria-describedby="areWitnessesError" name="Any witnesses or passengers" value={areWitnesses} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setAreWitnesses(e.target.value);
					localStorage.setItem("generalclaim-witnesses", e.target.value);
                }}>
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
				</select>
				<p id="areWitnessesError" className={showError && areWitnesses === "" ? "error" : "error-hidden"}>This field is required</p>
            </div>
		</div>
        <Collapse in={areWitnesses === "Yes"}>
			<div className="outline col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-bottom">
				<div>
					<div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
						<label className="control-label" htmlFor="witness-name">Witness Name:</label>
						<div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
							<input className="form-control" type="text" maxLength={55} id="witness-name" placeholder="John Smith" name="Witness Name" value={witnessName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								setWitnessName(e.target.value);
							}} />
						</div>
					</div>
					<div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
						<label className="control-label" htmlFor="witness-address">Witness Address:</label>
						<div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
							<input className="form-control" type="text" maxLength={55} id="witness-address" placeholder="John Smith 123 Main Street, Town, State" name="Witness Address" value={witnessAddress} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								setWitnessAddress(e.target.value);
							}} />
						</div>
					</div>
					<div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
						<label className="control-label" htmlFor="witness-primary-phone">Witness Primary Phone Number:</label>
						<div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
							<PhoneInput id="witness-primary-phone" placeholder="(123)-456-7890" name="Witness' primary phone" aria-invalid={showError && (witnessPrimaryPhone !== "" && !isValidPhoneNumber(witnessPrimaryPhone))} aria-describedby="witnessPrimaryPhoneError" country="US" international={false} className="form-control" value={witnessPrimaryPhone} maxLength={14} onChange={(phone: string) => {
								if (!phone) phone = "";
								setWitnessPrimaryPhone(phone);
							}} />
						</div>
						<p id="witnessPrimaryPhoneError" className={showError && (witnessPrimaryPhone !== "" && !isValidPhoneNumber(witnessPrimaryPhone)) ? "error" : "error-hidden"}>Please provide a valid phone number</p>
					</div>
					<div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
						<label className="control-label" htmlFor="witness-alternate-phone">Witness Alternate Phone Number:</label>
						<div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
							<PhoneInput id="witness-alternate-phone" placeholder="(123)-456-7890" name="Witness' alternate phone" aria-invalid={showError && (witnessAlternatePhone !== "" && !isValidPhoneNumber(witnessAlternatePhone))} aria-describedby="witnessAlternatePhoneError" country="US" international={false} className="form-control" value={witnessAlternatePhone} maxLength={14} onChange={(phone: string) => {
								if (!phone) phone = "";
								setWitnessAlternatePhone(phone);
							}} />
						</div>
						<p id="witnessAlternatePhoneError" className={showError && (witnessAlternatePhone !== "" && !isValidPhoneNumber(witnessAlternatePhone)) ? "error" : "error-hidden"}>Please provide a valid phone number</p>
					</div>
					<div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
						<label className="control-label" htmlFor="witness-email">Witness E-mail Address:</label>
						<div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
							<input className="form-control" type="email" aria-invalid={showError && witnessEmail !== "" && !ValidateEmail(witnessEmail)} aria-describedby="witnessEmailError" id="witness-email" placeholder="jsmith@domain.com" name="Policy Holder's Email" value={witnessEmail} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								setWitnessEmail(e.target.value.toLocaleLowerCase());
							}} />
							<p id="witnessEmailError" className={showError && (witnessEmail !== "" && !ValidateEmail(witnessEmail)) ? "error" : "error-hidden"}>Please provide a valid email</p>
						</div>
					</div>
				</div>
                <p><strong>*If there are any additional witnesses, please explain in the remarks section below</strong></p>
            </div>
        </Collapse>
		<div className="closing-remarks">
			<label className="control-label" htmlFor="remarks">Remarks:</label>
            <div>
                <textarea id="remarks" name="remarks" value={remarks} aria-describedby="closingRemarks" className="form-control" maxLength={700} rows={6} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setRemarks(e.target.value);
                }} />
				<p id="closingRemarks"><strong>Maximum 700 characters.  You have used {remarks.length} characters.</strong></p>
			</div>
		</div>
	</div>;

	function updateClaim() {
		claim.areWitnesses = areWitnesses === "Yes" ? true : false;
		claim.witnessName = witnessName;
		claim.witnessAddress = witnessAddress;
		claim.witnessPrimaryPhone = witnessPrimaryPhone;
		claim.witnessAlternatePhone = witnessAlternatePhone;
		claim.witnessEmail = witnessEmail;
        claim.remarks = remarks;
		return claim;
	}

	function goToStep(step: number) {
		goBack(step, updateClaim());
	}

    function submitClaim()
    {
        let claim = updateClaim();
        submit(claim);
        
    }
}
